import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Globe } from 'lucide-react';
import { cn } from '../../../utils/cn';
import {
    getCurrentTimezoneInfo,
    formatTimezoneDisplay,
} from '../utils/timezone-helpers';
import type { SupportedTimezone } from '../constants';
import { SUPPORTED_TIMEZONES } from '../constants';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
} from '../../../components/select';

interface TimePickerMobileProps {
    timeZone: string;
    startTimestamp: number;
    onTimeSelect?: (timestamp: number) => void;
    initialSelectedTime?: number;
    onTimezoneChange?: (timezone: string) => void;
    allowTimeZoneChange?: boolean;
    /** When provided, used for dropdown options (e.g. includes unsupported browser timezone) */
    availableTimezones?: string[];
    freeHours?: string[];
    startHour?: number;
    endHour?: number;
    startMinute?: number;
    endMinute?: number;
    stepMinutes?: number;
}

interface TimeSlot {
    key: string;
    timestamp: number;
    displayTime: string;
    period: 'AM' | 'PM';
    isDisabled: boolean;
}

const TimePickerMobile: React.FC<TimePickerMobileProps> = ({
    timeZone,
    startTimestamp,
    onTimeSelect,
    initialSelectedTime,
    onTimezoneChange,
    allowTimeZoneChange = true,
    availableTimezones,
    freeHours,
    startHour = 9,
    endHour = 16,
    startMinute = 0,
    endMinute = 0,
    stepMinutes = 60,
}) => {
    const [selectedTime, setSelectedTime] = useState<number | null>(
        initialSelectedTime || null,
    );
    const listContainerRef = useRef<HTMLDivElement | null>(null);

    const selectedDateTzInfo = getCurrentTimezoneInfo(
        timeZone,
        new Date(startTimestamp),
    );

    const timezoneOptions = useMemo(() => {
        if (availableTimezones?.length) {
            return availableTimezones.map((tzId) => ({
                key: tzId,
                timeZoneId: tzId,
            }));
        }
        return Object.entries(SUPPORTED_TIMEZONES).map(([key, tz]) => ({
            key,
            timeZoneId: tz.timeZoneId,
        }));
    }, [availableTimezones]);

    // Select value: when using availableTimezones, use timeZoneId; otherwise use SUPPORTED key
    const currentTimezoneKey = useMemo(() => {
        if (
            availableTimezones?.length &&
            availableTimezones.includes(timeZone)
        ) {
            return timeZone;
        }
        const entry = Object.entries(SUPPORTED_TIMEZONES).find(
            ([, tz]) => tz.timeZoneId === timeZone,
        );
        return entry?.[0];
    }, [timeZone, availableTimezones]);

    const timeSlots = useMemo(() => {
        const slots: TimeSlot[] = [];

        if (stepMinutes <= 0) return slots;

        const startTotalMinutes = startHour * 60 + startMinute;
        const endTotalMinutes = endHour * 60 + endMinute;

        if (endTotalMinutes < startTotalMinutes) return slots;

        const now = new Date();
        const nowInTargetTz = new Intl.DateTimeFormat('en-NZ', {
            timeZone: timeZone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        });
        const nowFormatted = nowInTargetTz.format(now);

        const selectedDateInTargetTz = new Intl.DateTimeFormat('en-NZ', {
            timeZone: timeZone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        });
        const selectedDateFormatted = selectedDateInTargetTz.format(
            new Date(startTimestamp),
        );

        const [todayDateStr, todayTimeStr] = nowFormatted.split(', ');
        if (!todayDateStr || !todayTimeStr) {
            throw new Error(`Invalid formatted date: ${nowFormatted}`);
        }

        const isToday = todayDateStr === selectedDateFormatted;

        // Get start of day timestamps for comparison
        const todayMidnight = new Date(now);
        todayMidnight.setHours(0, 0, 0, 0);
        const selectedDayMidnight = new Date(startTimestamp);
        selectedDayMidnight.setHours(0, 0, 0, 0);

        let currentMinutesOfDay = 0;
        if (isToday) {
            const [hourStr, minuteStr] = todayTimeStr.split(':');
            if (!hourStr || !minuteStr) {
                throw new Error(`Invalid time part from: ${todayTimeStr}`);
            }
            const parsedHour = parseInt(hourStr, 10);
            const parsedMinute = parseInt(minuteStr, 10);
            if (Number.isNaN(parsedHour) || Number.isNaN(parsedMinute)) {
                throw new Error(`Invalid numeric time from: ${todayTimeStr}`);
            }
            currentMinutesOfDay = parsedHour * 60 + parsedMinute;
        }

        const dateFormatter = new Intl.DateTimeFormat('en-NZ', {
            timeZone: timeZone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        });
        const timeFormatter = new Intl.DateTimeFormat('en', {
            timeZone: timeZone,
            hour12: false,
            hour: '2-digit',
            minute: '2-digit',
        });

        const selectedDate = new Date(startTimestamp);
        const dateInTargetTz = dateFormatter.format(selectedDate);
        const [day, month, year] = dateInTargetTz.split('/').map(Number);

        if (year === undefined || month === undefined || day === undefined) {
            throw new Error(`Invalid date components from: ${dateInTargetTz}`);
        }

        for (
            let minuteOfDay = startTotalMinutes;
            minuteOfDay < endTotalMinutes;
            minuteOfDay += stepMinutes
        ) {
            const hour = Math.floor(minuteOfDay / 60);
            const minute = minuteOfDay % 60;

            const testDate = new Date(year, month - 1, day, hour, minute, 0, 0);

            const displayedTime = timeFormatter.format(testDate);
            const [displayedHourStr, displayedMinuteStr] =
                displayedTime.split(':');
            if (!displayedHourStr || !displayedMinuteStr) {
                throw new Error(
                    `Invalid hour from displayed time: ${displayedTime}`,
                );
            }
            const displayedHour = parseInt(displayedHourStr, 10);
            const displayedMinute = parseInt(displayedMinuteStr, 10);

            let timeTimestamp = testDate.getTime();

            if (displayedHour !== hour || displayedMinute !== minute) {
                const diffMinutes =
                    (hour - displayedHour) * 60 + (minute - displayedMinute);
                timeTimestamp = testDate.getTime() + diffMinutes * 60 * 1000;
            }

            const normalizedTime = timeFormatter.format(
                new Date(timeTimestamp),
            );
            const [normalizedHourStr, normalizedMinuteStr] =
                normalizedTime.split(':');
            if (!normalizedHourStr || !normalizedMinuteStr) {
                throw new Error(`Invalid normalized time: ${normalizedTime}`);
            }
            const normalizedHour = parseInt(normalizedHourStr, 10);
            const normalizedMinute = parseInt(normalizedMinuteStr, 10);

            const slotMinutesOfDay = normalizedHour * 60 + normalizedMinute;

            let isDisabled = false;
            if (isToday) {
                isDisabled = slotMinutesOfDay <= currentMinutesOfDay;
            } else {
                isDisabled =
                    selectedDayMidnight.getTime() < todayMidnight.getTime();
            }

            const period = normalizedHour >= 12 ? 'PM' : 'AM';
            let hour12 = normalizedHour % 12;
            if (hour12 === 0) {
                hour12 = 12;
            }
            const displayTime = `${String(hour12).padStart(2, '0')}:${normalizedMinuteStr} ${period}`;

            // Backend returns labels in format "9:00 AM" (no leading zero, space before AM/PM)
            const backendFormatLabel = `${hour12}:${normalizedMinuteStr} ${period}`;

            const isInFreeHours =
                typeof freeHours === 'undefined'
                    ? true
                    : freeHours.includes(backendFormatLabel);

            if (!isInFreeHours) {
                isDisabled = true;
            }

            const normalizedLabel = `${normalizedHourStr}:${normalizedMinuteStr}`;

            slots.push({
                key: normalizedLabel,
                timestamp: timeTimestamp,
                displayTime,
                period,
                isDisabled,
            });
        }

        return slots;
    }, [
        timeZone,
        startTimestamp,
        freeHours,
        startHour,
        endHour,
        startMinute,
        endMinute,
        stepMinutes,
    ]);

    useEffect(() => {
        if (
            typeof initialSelectedTime === 'number' &&
            initialSelectedTime !== selectedTime
        ) {
            setSelectedTime(initialSelectedTime);
            return;
        }

        if (
            typeof initialSelectedTime === 'undefined' &&
            selectedTime !== null
        ) {
            setSelectedTime(null);
        }
    }, [initialSelectedTime, selectedTime]);

    useEffect(() => {
        if (!timeSlots.length) return;

        const container = listContainerRef.current;
        if (!container) return;

        const scheduledScroll = () => {
            const targetTimestamp =
                typeof initialSelectedTime === 'number'
                    ? initialSelectedTime
                    : timeSlots.find((slot) => !slot.isDisabled)?.timestamp;

            if (!targetTimestamp) return;

            const targetButton = container.querySelector<HTMLButtonElement>(
                `button[data-timestamp="${targetTimestamp}"]`,
            );

            const buttonToScroll =
                targetButton ??
                container.querySelector<HTMLButtonElement>(
                    'button:not(:disabled)',
                );

            buttonToScroll?.scrollIntoView({
                block: 'nearest',
                behavior: 'smooth',
            });
        };

        const animationId = requestAnimationFrame(scheduledScroll);

        return () => {
            cancelAnimationFrame(animationId);
        };
    }, [initialSelectedTime, timeSlots]);

    const timezoneCurrentTimeLabel = useMemo(() => {
        const formatter = new Intl.DateTimeFormat('en', {
            timeZone,
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
        });
        return formatter.format(new Date());
    }, [timeZone]);

    const handleTimeSelect = (timestamp: number, isDisabled: boolean) => {
        if (isDisabled) return;
        setSelectedTime(timestamp);
        if (onTimeSelect) {
            onTimeSelect(timestamp);
        }
    };

    const handleTimezoneChange = (value: string) => {
        const newTimeZoneId = availableTimezones?.includes(value)
            ? value
            : SUPPORTED_TIMEZONES[value as SupportedTimezone]?.timeZoneId;
        if (newTimeZoneId && onTimezoneChange) {
            onTimezoneChange(newTimeZoneId);
        }
    };

    return (
        <div className="relative h-70 w-full space-y-4">
            <div className="absolute -top-1 left-0 w-[101%] bg-white pb-1">
                <p className="mb-3 text-sm font-semibold text-[#161717]">
                    What time works best?
                </p>
                <Select
                    value={currentTimezoneKey ?? undefined}
                    onValueChange={handleTimezoneChange}
                    disabled={!allowTimeZoneChange}
                >
                    <SelectTrigger
                        className={cn(
                            'mb-3 flex w-full items-center justify-start gap-2 border-none bg-transparent p-0 text-left text-sm text-[#4F4F4F] hover:bg-transparent',
                            !allowTimeZoneChange && 'cursor-default opacity-50',
                        )}
                    >
                        <Globe
                            className="h-4 w-4 text-[#4F4F4F]"
                            aria-hidden="true"
                        />
                        <span>
                            {selectedDateTzInfo.shortName} (
                            {timezoneCurrentTimeLabel.toLowerCase()})
                        </span>
                    </SelectTrigger>
                    <SelectContent className="w-full">
                        {timezoneOptions.map(({ key, timeZoneId }) => (
                            <SelectItem key={key} value={key}>
                                {formatTimezoneDisplay(
                                    timeZoneId,
                                    new Date(startTimestamp),
                                )}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            <div ref={listContainerRef} className="h-70 overflow-auto pt-16">
                <div className="space-y-px pb-8">
                    {timeSlots
                        .filter((slot) => !slot.isDisabled)
                        .map((slot) => {
                            const isActive = slot.timestamp === selectedTime;
                            return (
                                <button
                                    key={slot.key}
                                    type="button"
                                    data-timestamp={slot.timestamp}
                                    onClick={() =>
                                        handleTimeSelect(
                                            slot.timestamp,
                                            slot.isDisabled,
                                        )
                                    }
                                    className={cn(
                                        'w-full rounded-[8px] border px-4 py-3 text-left text-base font-medium transition-colors',
                                        isActive
                                            ? 'border-[#161717] bg-[#161717] text-white'
                                            : 'border-[#DADADA] bg-transparent text-[#3F3F3F]',
                                    )}
                                >
                                    {slot.displayTime}
                                </button>
                            );
                        })}
                </div>
            </div>
        </div>
    );
};

export { TimePickerMobile };
